import type {
  AssetDto,
  ReportResponseDtoStatus as ReportResponseDtoStatusType,
} from '@/api/WhisApi/generated';
import { ReportResponseDtoStatus } from '@/api/WhisApi/generated';
import { FoldableSection } from '@/components/FoldableSection/FoldableSection';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { SizableText, XStack, YStack } from 'tamagui';
import { ReportDetailSection } from '../ReportDetailSection';
import { AssetPreviewModal } from '../ReportDetailSupportingDocuments/components/AssetPreviewModal';
import { SupportingDocumentRow } from '../ReportDetailSupportingDocuments/components/SupportingDocumentRow';
import { useAssetPreview } from '../ReportDetailSupportingDocuments/useAssetPreview';

interface ReportDetailRraSectionProps {
  status: ReportResponseDtoStatusType;
  rraAsset: AssetDto | null;
  defaultOpen?: boolean;
}

export const ReportDetailRraSection = ({
  status,
  rraAsset,
  defaultOpen = false,
}: ReportDetailRraSectionProps): React.ReactElement => {
  const { t } = useAppTranslation();
  const isPendingRra = status === ReportResponseDtoStatus.pendingRRA;
  const { previewUrl, previewName, openPreview, closePreview } =
    useAssetPreview();
  const displayRraAsset = rraAsset
    ? { ...rraAsset, name: t('reportDetail.rra.pdfLabel') }
    : null;

  return (
    <ReportDetailSection>
      <FoldableSection
        title={t('reportDetail.sections.rapidRiskAssessment')}
        defaultOpen={defaultOpen}
      >
        {isPendingRra ? (
          <XStack alignItems="center" gap="$2" flexWrap="wrap">
            <SizableText
              backgroundColor="$warningBg"
              color="$warningFont"
              size="$2"
              paddingHorizontal="$2"
              paddingVertical="$1"
            >
              {t('reportDetail.rra.pendingState')}
            </SizableText>
            <SizableText color="$secondary75">
              {t('reportDetail.rra.pendingDescription')}
            </SizableText>
          </XStack>
        ) : (
          <YStack gap="$3">
            {displayRraAsset ? (
              <SupportingDocumentRow
                asset={displayRraAsset}
                onPreviewImage={openPreview}
              />
            ) : (
              <SizableText size="$2" color="$secondary50">
                {t('reportDetail.supportingDocuments.empty')}
              </SizableText>
            )}
          </YStack>
        )}
      </FoldableSection>
      <AssetPreviewModal
        previewUrl={previewUrl}
        previewName={previewName}
        onClose={closePreview}
      />
    </ReportDetailSection>
  );
};
